import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Users, PencilLine, TrendingUp, FolderKanban, Layers, Crown, UserCheck, Database, Sparkles } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import { SCREENSHOTS } from "@/config/site";
import { usePageMeta } from "@/lib/meta";

const CAPABILITIES = [
  { icon: Sparkles, label: "AI-assisted practice planning" },
  { icon: Users, label: "Team & roster management" },
  { icon: ClipboardList, label: "Lineup creation & organization" },
  { icon: PencilLine, label: "Game & practice notes" },
  { icon: TrendingUp, label: "Player development tracking" },
  { icon: FolderKanban, label: "Coaching organization tools" },
  { icon: Layers, label: "Multiple-team support" },
  { icon: Crown, label: "Head Coach controls" },
  { icon: UserCheck, label: "Assistant Coach collaboration" },
  { icon: Database, label: "Centralized coaching information" },
];

const DEEP_DIVES = [
  {
    screenshot: SCREENSHOTS[0],
    title: "Practice plans that run themselves",
    text: "Tell CoachER the focus — fielding, hitting, baserunning — and get a complete timed practice with setup notes, step-by-step drill instructions, and coaching points for every station. Show up with a plan, not a blank notebook.",
    points: ["Timed stations from warm-up to wrap-up", "Drill setup, run-it instructions & coaching points", "Equipment lists so nothing gets left behind", "Tagged by focus area for the whole season"],
  },
  {
    screenshot: SCREENSHOTS[1],
    title: "Lineups without the parking-lot scramble",
    text: "Build your fielding lineup inning by inning — all nine positions plus the bench — with AI Suggest to handle the first draft. Fair exposure for every player, decided before you pull into the lot.",
    points: ["Inning-by-inning field view", "Bench management built in", "AI Suggest for instant first drafts", "Edit positions in seconds"],
  },
  {
    screenshot: SCREENSHOTS[2],
    title: "A batting order with reasoning",
    text: "CoachER builds your batting order from your own coaching notes, player development, and handedness — and tells you why each hitter sits where they do. Reorder with a tap when your gut says otherwise.",
    points: ["Built from your coaching notes", "Reasoning shown for every spot", "One-tap reordering", "Development-aware placement"],
  },
  {
    screenshot: SCREENSHOTS[3],
    title: "A staff, not a solo act",
    text: "Head Coaches run the dugout; Assistant Coaches jump in and help — without buying their own subscription. Everyone works from the same roster, the same lineups, the same plan.",
    points: ["Head Coach & Assistant roles", "Assistants join at no extra cost", "Permissions set by the Head Coach", "One shared team workspace"],
  },
];

const Features = () => {
  usePageMeta(
    "Features | CoachER",
    "AI practice plans, field lineup builder, AI batting order, rosters, notes, and player development — see everything CoachER does for youth baseball & softball coaches.",
    "/features"
  );

  return (
    <div data-testid="features-page">
      <section className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
            Features
          </p>
          <MaskedLines
            lines={["Everything between", "first practice and", "the final lineup card."]}
            delay={0.15}
            className="mt-6 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase leading-[1.0] tracking-tight text-white"
            lineClassName="[&:nth-child(3)]:text-outline-white"
          />
        </div>
      </section>

      <Marquee items={["Practice plans", "Field lineups", "Batting orders", "Player development", "One digital dugout"]} />

      {/* Deep dives */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl space-y-24 px-4 sm:px-8 sm:space-y-32">
          {DEEP_DIVES.map((d, i) => (
            <div key={d.title} data-testid={`feature-deep-${i}`} className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20`}>
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="relative mx-auto max-w-xs sm:max-w-sm">
                  <div className="absolute -inset-4 rounded-3xl bg-coacher-surface pinstripes" aria-hidden="true" />
                  <img
                    src={d.screenshot.src}
                    alt={d.title}
                    loading="lazy"
                    className={`relative rounded-2xl border border-coacher-border shadow-frame ${i % 2 === 1 ? "rotate-1" : "-rotate-1"}`}
                  />
                  <span className="absolute -top-6 -left-2 font-varsity text-5xl text-outline-navy select-none" aria-hidden="true">{d.screenshot.num}</span>
                </div>
              </Reveal>
              <Reveal delay={0.12} className={i % 2 === 1 ? "lg:order-1" : ""}>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold uppercase leading-[1.05] tracking-tight text-coacher-navy">{d.title}</h2>
                <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">{d.text}</p>
                <ul className="mt-7 space-y-3">
                  {d.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm font-medium text-coacher-navy">
                      <span className="inline-block h-1.5 w-1.5 rotate-45 bg-coacher-red" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* Capability grid */}
      <section data-testid="features-capabilities" className="bg-coacher-surface py-20 sm:py-28 pinstripes">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal className="max-w-3xl">
            <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-coacher-red">
              <span className="inline-block h-px w-8 bg-coacher-red" aria-hidden="true" />
              What you get
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight text-coacher-navy">
              One digital dugout for your team
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.04}>
                <div data-testid={`capability-${i}`} className="flex h-full items-center gap-3.5 rounded-lg border border-coacher-border bg-white p-5 shadow-card transition-colors duration-200 hover:border-coacher-navy/40">
                  <c.icon size={20} className="shrink-0 text-coacher-navy" strokeWidth={2} />
                  <p className="text-sm font-semibold text-coacher-navy">{c.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-coacher-navy py-20 sm:py-24">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold uppercase tracking-tight text-white">
              See it in your dugout.
            </h2>
            <Link
              to="/pricing"
              data-testid="features-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-md bg-coacher-red px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
            >
              Start Your Free Trial <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Features;
